import { getVersion } from '@tauri-apps/api/app'
import { invoke } from '@tauri-apps/api/core'
import { Image } from '@tauri-apps/api/image'
import { getCurrentWebview } from '@tauri-apps/api/webview'
import { getCurrentWindow } from '@tauri-apps/api/window'
import {
  readText,
  writeText,
  writeImage,
} from '@tauri-apps/plugin-clipboard-manager'
import { getCurrent, onOpenUrl } from '@tauri-apps/plugin-deep-link'
import { writeFile, BaseDirectory } from '@tauri-apps/plugin-fs'
import { impactFeedback } from '@tauri-apps/plugin-haptics'
import { openUrl } from '@tauri-apps/plugin-opener'
import * as OS from '@tauri-apps/plugin-os'
import { relaunch } from '@tauri-apps/plugin-process'
import { check } from '@tauri-apps/plugin-updater'
import { nanoid } from 'nanoid'
import { match } from 'ts-pattern'

import { Redacted, Result } from '@blade/std-lib'
import { LOG_TAURI_INVOKE_ENABLED } from '@root/shared/environment'
import { globalStore } from '@root/shared/lib/tauri/tauri-store'

import { copyToClipboard } from '../DomUtil'
import { UnknownError } from '../Error'

import { DeviceId } from './device-id'
import { PlatformError } from './platform-error'
import { getFallbackDeviceIdFromLocalStorage } from './shared-platform-util'

import type * as PlatformUtil from './platform-util'
import type { Update } from '@tauri-apps/plugin-updater'

const setScaleFactor = async (scaleFactor: number) => {
  const result = await Result.tryPromise({
    try: () => getCurrentWebview().setZoom(scaleFactor),
    catch: (cause) =>
      new PlatformError({
        platform: 'tauri',
        method: 'setZoom',
        cause,
      }),
  })

  return result.pipe(
    Result.map(() => scaleFactor),
    Result.mapError((error) => [error, 1] as [typeof error, number]),
  )
}

const getStoreDeviceId = async (): Promise<DeviceId | undefined> => {
  const result = await globalStore.get<string>('device-id')
  if (Result.isFailure(result)) {
    return undefined
  }
  if (!result.success) {
    const newDeviceId = DeviceId(nanoid())
    await globalStore.set('device-id', newDeviceId)
    return newDeviceId
  }

  return DeviceId(result.success)
}

let cached: DeviceId | undefined

export const TauriPlatformUtil: PlatformUtil.PlatformUtil = {
  platform: 'tauri' as const,
  is: (platform) => platform === 'tauri',
  openLink: (href: URL | string) =>
    Result.tryPromise({
      try: () => openUrl(href),
      catch: (cause) =>
        new PlatformError({
          platform: 'tauri',
          method: 'openLink',
          cause,
        }),
    }),
  getDeviceId: async () => {
    if (cached !== undefined) {
      return cached
    }

    try {
      const machineId = DeviceId(await invoke<string>('get_machine_id'))
      if (machineId) {
        cached = machineId
        return machineId
      }
    } catch {
      const deviceId = await getStoreDeviceId()
      if (deviceId) {
        cached = deviceId
        return deviceId
      }
    }

    const deviceId = getFallbackDeviceIdFromLocalStorage()

    cached = deviceId
    return deviceId
  },
  setScaleFactor,
  getVersion: () => getVersion(),
  locale: async () => (await OS.locale()) ?? undefined,
  impactFeedback: async (style) => {
    await impactFeedback(style)
  },
  share: (data) =>
    Result.tryPromise({
      try: () => navigator.share(data),
      catch: (cause) =>
        new PlatformError({
          platform: 'tauri',
          method: 'share',
          cause,
        }),
    }),
  copyImageToClipboard: async (blob: Blob, _mimeType: string) => {
    const bitmap = await createImageBitmap(blob)

    const result = Result.tryPromise({
      try: async () => {
        const canvas = document.createElement('canvas')
        canvas.width = bitmap.width
        canvas.height = bitmap.height

        const ctx = canvas.getContext('2d')!
        ctx.drawImage(bitmap, 0, 0)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const rgba = new Uint8Array(imageData.data.buffer)

        const image = await Image.new(rgba, canvas.width, canvas.height)

        await writeImage(image)
      },
      catch: (cause) =>
        new PlatformError({
          platform: 'tauri',
          method: 'copyImageToClipboard',
          cause,
        }),
    })

    bitmap.close()

    return result
  },
  copyToClipboard: async (text) => {
    const textToCopy = Redacted.isRedacted(text) ? Redacted.value(text) : text
    try {
      await writeText(textToCopy)
      document.dispatchEvent(
        new CustomEvent('app:copy', { detail: { text: textToCopy } }),
      )
      return true
    } catch {
      /* empty */
    }

    const copied = await copyToClipboard(text)
    return copied
  },
  readFromClipboard: async () => {
    try {
      const text = await readText()
      return Result.succeed(text)
    } catch (error) {
      return Result.fail(UnknownError.fromUnknown(error))
    }
  },
  hostPlatform: OS.platform,
  deeplink: {
    getCurrent: async () => (await getCurrent()) ?? undefined,
    onOpenUrl,
  },
  window: {
    minimize: () => getCurrentWindow().minimize(),
    toggleMaximize: () => getCurrentWindow().toggleMaximize(),
    close: () => getCurrentWindow().close(),
  },
  downloadFileFromURL: async () =>
    Result.fail(
      new PlatformError({
        platform: 'tauri',
        method: 'downloadFileFromURL',
        cause: new Error('not supported'),
      }),
    ),
  downloadFile: async (blob, filename) =>
    Result.tryPromise({
      try: async () => {
        const arrayBuffer = await blob.arrayBuffer()
        const contents = new Uint8Array(arrayBuffer)
        return writeFile(filename, contents, {
          baseDir: BaseDirectory.Download,
        })
      },
      catch: (cause) =>
        new PlatformError({
          platform: 'tauri',
          method: 'downloadFile',
          cause,
        }),
    }),
  relaunch: async () =>
    Result.tryPromise({
      try: () => relaunch(),
      catch: (cause) =>
        new PlatformError({
          platform: 'tauri',
          method: 'relaunch',
          cause,
        }),
    }),
  check: async (options) => {
    const checkResult = await Result.tryPromise({
      try: async () => {
        if (LOG_TAURI_INVOKE_ENABLED) {
          // eslint-disable-next-line no-console
          console.log('[PLATFORM REQUEST]: check', { options })
        }
        const result = await check(options)
        if (LOG_TAURI_INVOKE_ENABLED) {
          // eslint-disable-next-line no-console
          console.log('[PLATFORM RESPONE]: check', {
            options,
            response: result,
          })
        }

        if (result) {
          return {
            currentVersion: result.currentVersion,
            version: result.version,
            notes: result.rawJson.notes?.toString(),
            install: () => installUpdate(result),
            download: async (options) => {
              const downloadResult = await Result.tryPromise({
                try: () =>
                  result.download((event) =>
                    match(event)
                      .with({ event: 'Started' }, ({ data }) =>
                        options.onStarted(data),
                      )
                      .with({ event: 'Progress' }, ({ data }) =>
                        options.onProgress(data),
                      )
                      .with({ event: 'Finished' }, () => options.onFinished())
                      .exhaustive(),
                  ),
                catch: (cause) =>
                  new PlatformError({
                    platform: 'tauri',
                    method: 'Update.download',
                    cause,
                  }),
              })

              if (Result.isFailure(downloadResult)) {
                // eslint-disable-next-line no-console
                console.error({ downloadResult })
              }

              return downloadResult
            },
          } satisfies PlatformUtil.PlatformUpdate
        }
      },
      catch: (cause) =>
        new PlatformError({
          platform: 'tauri',
          method: 'check',
          cause,
        }),
    })

    if (Result.isFailure(checkResult)) {
      // eslint-disable-next-line no-console
      console.error({ checkResult })
    }

    return checkResult
  },
}

const installUpdate = async (
  update: Update,
): Promise<Result.Result<void, PlatformError>> => {
  const installUpdateResult = await Result.tryPromise({
    try: () => update.install(),
    catch: (cause) =>
      new PlatformError({
        platform: 'tauri',
        method: 'Update.install',
        cause,
      }),
  })

  if (Result.isFailure(installUpdateResult)) {
    // eslint-disable-next-line no-console
    console.error({ installUpdateResult })
  }

  return installUpdateResult
}
